import { useQuery } from '@apollo/client'
import { Stack, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import itemIconBlack from '@/assets/icons/items_black.svg'
import itemIconWhite from '@/assets/icons/items_white.svg'
import useProfilePath from '@/hooks/useProfilePath'
import { useAuth } from '@/plugins/auth'
import { GetTotals, QUERY_GET_TOTALS } from '@/schemas/explore.schema'
import { formatValueToSuffix } from '@/utils/utils'

import RootButton from '../Buttons/RootButton'
import IconWrapper from '../MediaWrappers/IconWrapper'
import PageSelector from '../PageSelector'
import { Totals } from './ExplorePage'

interface ExploreHeaderProps {
  tabs: { id: string; title: string }[]
  currTabId: string
  totals: Totals
  setTotals: (totals: Totals) => void
  triggerRefetch: boolean
}

const ExploreHeader: React.FC<ExploreHeaderProps> = ({
  tabs,
  currTabId,
  totals,
  setTotals,
  triggerRefetch,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { address } = useAuth()
  const navigate = useNavigate()
  const profilePath = useProfilePath(address)

  // Get totals
  const { data, refetch } = useQuery<GetTotals>(QUERY_GET_TOTALS)
  const totalCreators = useMemo(() => data?.users.agg.count || 0, [data])
  const totalItems = useMemo(() => data?.items.agg.count || 0, [data])
  const totalCollections = useMemo(() => data?.collections.agg.count || 0, [data])

  // Handle Tab Change
  const handleTabChange = useCallback(
    (id: string) => {
      navigate(`/explore/${id}`)
      refetch()
    },
    [navigate, refetch]
  )

  // Trigger refetch
  useEffect(() => {
    refetch()
  }, [triggerRefetch])

  // Update totals state
  useEffect(() => {
    setTotals({ creators: totalCreators, items: totalItems, collections: totalCollections })
  }, [totalCreators, totalItems, totalCollections, setTotals])

  // Process tabs with new totals info
  const processedTabs = useMemo(
    () =>
      tabs.map(tab => ({
        ...tab,
        // define total by comparing tab.id with key of Totals interface (totals object)
        total: totals[tab.id as keyof Totals],
      })),
    [tabs, totals]
  )

  return (
    <Stack
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
      }}
    >
      <Stack
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: '40px',
        }}
      >
        {processedTabs.map(tab => (
          <PageSelector
            key={tab.id}
            active={currTabId === tab.id}
            onClick={() => handleTabChange(tab.id)}
            text={tab.title}
            count={formatValueToSuffix(tab.total || 0, 4, 0)}
          />
        ))}
      </Stack>
      {address && (
        <RootButton
          secondary
          onClick={() => navigate(profilePath)}
          style={{
            whiteSpace: 'pre-wrap',
          }}
        >
          {isMobile ? (
            <IconWrapper iconDark={itemIconWhite} iconLight={itemIconBlack} />
          ) : (
            t('components.profile.MyItems', 'My Items')
          )}
        </RootButton>
      )}
    </Stack>
  )
}

export default ExploreHeader
